// Sentry initialisation — env-gated. No-op unless REACT_APP_SENTRY_DSN is set.
//
// Wired first in src/index.js so import-time errors are captured. DSN is
// intentionally read from build-time env (CRA), letting us push the code
// today and inject the real DSN via the Deploy workflow later.
import * as Sentry from "@sentry/react";

const dsn = (process.env.REACT_APP_SENTRY_DSN || "").trim();

// Forbidden field names. Kept in sync with backend/veralok/telemetry_scrub.py
// so both boundaries scrub the identical vocabulary.
const FORBIDDEN_KEYS = new Set([
  "ciphertext_b64",
  "wrapped_private_key_b64",
  "private_key_b64",
  "private_key_b64_do_not_upload",
  "private_key",
  "passphrase",
  "wrap_key",
  "plaintext",
  "plaintext_utf8",
  "payload",
  "document",
  "attachments",
  "bytes_b64",
]);

const REDACT = "<redacted-by-veralok>";

export function scrubTelemetry(value) {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) return value.map(scrubTelemetry);
  if (typeof value === "object") {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      if (typeof k === "string" && FORBIDDEN_KEYS.has(k.toLowerCase())) {
        out[k] = REDACT;
      } else {
        out[k] = scrubTelemetry(v);
      }
    }
    return out;
  }
  return value;
}

export function initSentry() {
  if (!dsn) {
    // Explicit info so DevTools makes it obvious we're not shipping crash
    // reports from local dev accidentally.
    // eslint-disable-next-line no-console
    console.info("Sentry disabled (REACT_APP_SENTRY_DSN not set)");
    return false;
  }
  Sentry.init({
    dsn,
    environment: process.env.REACT_APP_SENTRY_ENV || process.env.NODE_ENV || "production",
    release: process.env.REACT_APP_SENTRY_RELEASE || "veralok-frontend@1.0.0",
    tracesSampleRate: parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || "0"),
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // No PII: VeraLok is a Zero-Knowledge identity product.
    sendDefaultPii: false,
    // Bound the outbound event to guarantee disclosure ciphertext,
    // wrapped private keys and passphrases cannot escape the browser
    // via a Sentry envelope on an unhandled exception.
    beforeSend(event) {
      return scrubTelemetry(event);
    },
    beforeBreadcrumb(breadcrumb) {
      return scrubTelemetry(breadcrumb);
    },
  });
  return true;
}

export { Sentry };

